import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Divider from "@material-ui/core/Divider";
import MobileStepper from "@material-ui/core/MobileStepper";
import Button from "@material-ui/core/Button";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import ListSubheader from "@material-ui/core/ListSubheader";
import Typography from "@material-ui/core/Typography";

// Icons
import { TargetIcon, ThicknessIcon } from "../../constants/customIcons";
import AddIcon from "@material-ui/icons/Add";

// Other
import { v4 } from "uuid";
import Notes from "../Results/Notes";
import { customTheme } from "../../constants/customTheme";
import { colors } from "../../constants/colors";
import { photoSampleEnabled } from "../../constants/constants";
// Actions
import {
  setVirtualKeyboard,
  requestPicture,
  createSample,
} from "../../actions";

const useStyles = makeStyles({
  root: {
    flex: "1 1 auto",
    height: "100%",
    zIndex: 1,
  },
  headerSpace: {
    height: "35px",
  },
  locationInfo: (props) => ({
    height: "100%",
    overflowY: "auto",
    padding: " 0px 20px 20px 36px",
    "& h1": {
      marginBottom: "20px",
    },
    "& .MuiListItem-root": {
      paddingLeft: "0px",
    },
    ...customTheme(props.theme).customScrollBar,
  }),
  listIcon: customTheme().iconMedium,
  listText: {
    "& span": {
      fontSize: "1rem",
    },
    "& p": {
      fontSize: "1rem",
    },
  },
  emptyContainer: {
    height: "100px",
  },
  iconButton: (props) => ({
    ...customTheme(props.theme).iconButton,
    "& .MuiSvgIcon-root": {
      ...customTheme().iconMedium,
    },
  }),
  divider: {
    marginTop: "15px",
    backgroundColor: colors.disabledColor,
  },
  listSubheader: {
    height: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1rem",
    fontWeight: 400,
  },
  illustrationContainer: {
    width: "90%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  illustrationStepper: {
    width: "60%",
    backgroundColor: (props) => colors[props.theme].bkg3,
  },
  acqInstructionImage: {
    maxWidth: "50%",
  },
  listTitle: {
    fontWeight: 600,
    fontSize: "1.5rem",
    textAlign: "center",
    padding: "10px",
  },
});

const LocationInfoSection = (props) => {
  const classes = useStyles(props);

  useEffect(() => {
    return function cleanup() {
      // close virtual keyboard on unmount
      props.setVirtualKeyboard(false);
    };
  });
  const [activeStep, setActiveStep] = React.useState(0);

  const handleAddPhoto = () => {
    let photoUuid = v4();
    props.createSample(photoUuid, "PICTURE");
    props.requestPicture(photoUuid);
  };

  const handleRotateIllustration = (direction) => {
    if (direction === "NEXT") {
      setActiveStep(activeStep + 1);
    } else if (direction === "BACK") {
      setActiveStep(activeStep - 1);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.headerSpace} />
      <div className={classes.locationInfo}>
        <Typography
          variant="h4"
          className={classes.listTitle}
          color="textPrimary"
        >
          Location Information
        </Typography>
        <List>
          <Divider className={classes.divider} />
          <ListSubheader disableSticky className={classes.listSubheader}>
            Progress
          </ListSubheader>

          <ListItem>
            <ListItemIcon>
              <TargetIcon className={classes.listIcon} />
            </ListItemIcon>
            <ListItemText
              className={classes.listText}
              primary={`E-Mode progress: ${props.locationProcessed["CSI"]}/${props.locationNeeded["CSI"]}`}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ThicknessIcon className={classes.listIcon} />
            </ListItemIcon>
            <ListItemText
              className={classes.listText}
              primary={`T-Mode progress: ${props.locationProcessed["BMODE"]}/${props.locationNeeded["BMODE"]}`}
            />
          </ListItem>

          {props.skinScore && (
            <React.Fragment>
              <Divider className={classes.divider} />
              <ListSubheader disableSticky className={classes.listSubheader}>
                Location Evaluation
              </ListSubheader>

              <ListItem>
                <ListItemText
                  className={classes.listText}
                  primary={`Skin score: ${props.skinScore}`}
                />
              </ListItem>
            </React.Fragment>
          )}

          {(props.locInstruction.text ||
            props.locInstruction.illustration.length > 0) && (
            <React.Fragment>
              <Divider className={classes.divider} />
              <ListSubheader disableSticky className={classes.listSubheader}>
                Acquisition Instructions
              </ListSubheader>
            </React.Fragment>
          )}
          {props.locInstruction.text && (
            <ListItem>
              <ListItemText
                className={classes.listText}
                primary={
                  props.locInstruction.text ? props.locInstruction.text : ""
                }
              />
            </ListItem>
          )}
          {props.locInstruction.illustration.length > 0 && (
            <div className={classes.illustrationContainer}>
              <img
                alt={"acquisition instruction"}
                className={classes.acqInstructionImage}
                src={props.locInstruction.illustration[activeStep].image}
              />
              {props.locInstruction.illustration.length > 1 && (
                <MobileStepper
                  variant="dots"
                  steps={props.locInstruction.illustration.length}
                  position="static"
                  activeStep={activeStep}
                  className={classes.illustrationStepper}
                  nextButton={
                    <Button
                      size="small"
                      onClick={() => handleRotateIllustration("NEXT")}
                      disabled={
                        activeStep ===
                        props.locInstruction.illustration.length - 1
                      }
                    >
                      Next
                      <KeyboardArrowRight />
                    </Button>
                  }
                  backButton={
                    <Button
                      size="small"
                      onClick={() => handleRotateIllustration("BACK")}
                      disabled={activeStep === 0}
                    >
                      <KeyboardArrowLeft />
                      Back
                    </Button>
                  }
                />
              )}
            </div>
          )}
          <Divider className={classes.divider} />
          <ListSubheader
            disableSticky
            className={classes.listSubheader}
          ></ListSubheader>
          <ListItem>
            <ListItemIcon>
              <IconButton
                className={classes.iconButton}
                onClick={() => handleAddPhoto()}
                disabled={!photoSampleEnabled}
              >
                <AddIcon />
              </IconButton>
            </ListItemIcon>
            <ListItemText
              className={classes.listText}
              primary={`Add photo sample to location`}
            />
          </ListItem>
        </List>
        <Notes prompt="LOCATION" />

        <Grid item className={classes.emptyContainer}></Grid>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      setVirtualKeyboard,
      requestPicture,
      createSample,
    },
    dispatch
  );
};

const mapStateToProps = (state) => {
  const location = state.locations.byId[state.locations.selectedLocationId];
  return {
    locationNeeded: location.samplesNeeded,
    locationProcessed: location.samplesProcessed,
    skinScore: location.skinScore,
    locInstruction:
      state.protocol.locations[state.locations.selectedLocationId].instruction,
    theme: state.utility.theme,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LocationInfoSection);
